"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SplitLayout = void 0;
const react_1 = __importStar(require("react"));
const element_resize_event_1 = __importStar(require("element-resize-event"));
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const core_1 = require("@veeam/core");
const helpers_1 = require("../../helpers");
const getSectionStyles_1 = require("./helpers/getSectionStyles");
const SplitLayoutInteractor_1 = require("./interactor/SplitLayoutInteractor");
const SplitLayout_styles_1 = require("./SplitLayout.styles");
const Popover_1 = require("../../Popover");
const StackView_1 = require("../../StackView");
const View_1 = require("../../View");
const SplitLayout = ({ direction = StackView_1.STACK_DIRECTION.row, start, end, startConfig, endConfig, resizable = true, onSizeChange, className, style, ['data-selector-id']: initialDataSelectorId }) => {
    const isRow = (0, react_1.useMemo)(() => direction === StackView_1.STACK_DIRECTION.row, [direction]);
    const configModel = (0, core_1.useModelBuilder)({
        isRow,
        startLimit: { minSize: (startConfig === null || startConfig === void 0 ? void 0 : startConfig.minSize) || 0, maxSize: (startConfig === null || startConfig === void 0 ? void 0 : startConfig.maxSize) || 0 },
        endLimit: { minSize: (endConfig === null || endConfig === void 0 ? void 0 : endConfig.minSize) || 0, maxSize: (endConfig === null || endConfig === void 0 ? void 0 : endConfig.maxSize) || 0 }
    }, [startConfig, endConfig]);
    const [rootRef, measureManager] = (0, Popover_1.useMeasureClientRect)();
    const [startRef, startMeasureManager] = (0, Popover_1.useMeasureClientRect)();
    const [endRef] = (0, Popover_1.useMeasureClientRect)();
    const dataSelectorId = (0, react_1.useMemo)(() => initialDataSelectorId || 'split-layout', [initialDataSelectorId]);
    const { interactor } = (0, core_1.useInteractor)(() => {
        return new SplitLayoutInteractor_1.SplitLayoutInteractor(configModel);
    });
    (0, react_1.useEffect)(() => {
        const rootStream = new rxjs_1.Observable((subscriber) => {
            const cb = () => {
                subscriber.next(true);
            };
            if (rootRef.current) {
                (0, element_resize_event_1.default)(rootRef.current, cb);
            }
            return () => {
                if (rootRef.current) {
                    (0, element_resize_event_1.unbind)(rootRef.current, cb);
                }
            };
        });
        const sub = interactor.sizeModel
            .asObservable()
            .pipe((0, operators_1.switchMap)((v) => rootStream.pipe((0, operators_1.startWith)(true), (0, operators_1.map)(() => v))))
            .subscribe((value) => {
            requestAnimationFrame(() => {
                var _a, _b, _c, _d;
                if (typeof window !== 'undefined' && startRef.current) {
                    const rootSize = measureManager.measure();
                    const rootWidth = (_a = rootSize === null || rootSize === void 0 ? void 0 : rootSize.width) !== null && _a !== void 0 ? _a : 0;
                    const rootHeight = (_b = rootSize === null || rootSize === void 0 ? void 0 : rootSize.height) !== null && _b !== void 0 ? _b : 0;
                    Object.assign(startRef.current.style, (0, getSectionStyles_1.getSectionStyles)({
                        config: startConfig,
                        modelValue: value,
                        isColumnDirection: direction === StackView_1.STACK_DIRECTION.column,
                        hasDefaultFlexBasisStyle: true
                    }));
                    Object.assign((_d = (_c = endRef.current) === null || _c === void 0 ? void 0 : _c.style) !== null && _d !== void 0 ? _d : {}, (0, getSectionStyles_1.getSectionStyles)({
                        config: endConfig,
                        modelValue: Object.assign(Object.assign({}, value), { width: rootWidth - value.width, height: rootHeight - value.height }),
                        isColumnDirection: direction === StackView_1.STACK_DIRECTION.column,
                        hasDefaultFlexBasisStyle: !(startConfig === null || startConfig === void 0 ? void 0 : startConfig.size)
                    }));
                }
            });
        });
        return () => {
            sub.unsubscribe();
        };
    });
    interactor.presenter = {
        measureRootBox() {
            return measureManager.measure() || { width: 0, height: 0 };
        },
        measureStartBox() {
            return startMeasureManager.measure() || { width: 0, height: 0 };
        }
    };
    interactor.onSizeChange = onSizeChange;
    interactor.isRow = isRow;
    const startStyles = (0, react_1.useMemo)(() => (0, getSectionStyles_1.getSectionStyles)({
        config: startConfig,
        modelValue: interactor.sizeModel.getValue(),
        isColumnDirection: direction === StackView_1.STACK_DIRECTION.column,
        hasDefaultFlexBasisStyle: true
    }), [direction, interactor.sizeModel, startConfig]);
    const endStyles = (0, react_1.useMemo)(() => (0, getSectionStyles_1.getSectionStyles)({
        config: endConfig,
        hasDefaultFlexBasisStyle: !(startConfig === null || startConfig === void 0 ? void 0 : startConfig.size)
    }), [endConfig, startConfig === null || startConfig === void 0 ? void 0 : startConfig.size]);
    if (interactor.sizeModel.getValue().shouldApply) {
        endStyles.flexGrow = 1;
    }
    return (react_1.default.createElement(StackView_1.StackView, { root: true, style: style, ref: rootRef, direction: direction, spaceFill: View_1.SPACE_FILL.all, "data-selector-id": dataSelectorId, "data-split-layout": true, className: (0, helpers_1.cn)('vm-SplitLayout', className) },
        !!start && (react_1.default.createElement(SplitLayout_styles_1.StyledSplitLayoutSection, { ref: startRef, style: startStyles }, start)),
        react_1.default.createElement(SplitLayout_styles_1.StyledSplitLayoutSeparator, { isRow: isRow, onDragStart: interactor.didResizeStart, onDragEnd: interactor.didResizeEnd, onMouseMove: interactor.didMouseMove, resizable: resizable }),
        !!end && (react_1.default.createElement(SplitLayout_styles_1.StyledSplitLayoutSection, { ref: endRef, style: endStyles }, end))));
};
exports.SplitLayout = SplitLayout;
//# sourceMappingURL=SplitLayout.js.map